import { 
  generateImage, 
  StabilityAIStylePreset, 
  base64ToFile,
  createEventCoverPrompt,
  getEventImageNegativePrompt,
  generateSocialMediaImage,
  getPlaceholderImage
} from './stabilityAIService-new';
import { nanoid } from 'nanoid';
import { db } from '../firebase/config';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';

/**
 * Different types of visual assets
 */
export type AssetType = 'banner' | 'social' | 'flyer';

/**
 * Different design styles for visual assets
 */
export type DesignStyle = 
  | 'modern' 
  | 'minimalist' 
  | 'vibrant' 
  | 'corporate' 
  | 'artistic' 
  | 'vintage';

/**
 * Standard dimensions for different asset types
 */
export const ASSET_DIMENSIONS = {
  banner: { width: 1200, height: 300 },
  social: { width: 1080, height: 1080 },
  flyer: { width: 800, height: 1100 },
  facebook: { width: 1200, height: 630 },
  instagram: { width: 1080, height: 1080 },
  twitter: { width: 1200, height: 675 },
  linkedin: { width: 1200, height: 627 }
};

/**
 * Map design styles to Stability AI style presets
 */
const STYLE_PRESET_MAP: Record<DesignStyle, StabilityAIStylePreset> = {
  modern: 'photographic',
  minimalist: 'enhance',
  vibrant: 'digital-art',
  corporate: 'photographic',
  artistic: 'fantasy-art',
  vintage: 'analog-film'
};

/**
 * Interface for visual asset generation request
 */
export interface VisualAssetRequest {
  prompt?: string;
  eventTitle: string;
  eventDescription: string;
  eventType: string;
  eventDate: Date | string;
  eventLocation: string;
  assetType: AssetType;
  style: DesignStyle;
  dimensions?: {
    width: number;
    height: number;
  };
}

/**
 * Interface for visual asset generation response
 */
export interface VisualAssetResponse {
  imageUrl: string;
  prompt: string;
  metadata?: any;
}

/**
 * Interface for a saved visual asset
 */
export interface SavedAsset {
  id: string;
  imageUrl: string;
  title: string;
  description?: string;
  type: AssetType;
  style?: DesignStyle;
  prompt?: string;
  dimensions?: {
    width: number;
    height: number;
  };
  createdAt: string;
  updatedAt?: string;
}

/**
 * Generate a placeholder data URL for when Stability AI fails
 */
const generatePlaceholderImage = (
  type: AssetType,
  title: string,
  dimensions: { width: number, height: number },
  style: DesignStyle
): string => {
  const colors: Record<DesignStyle, { bg: string, text: string }> = {
    modern: { bg: '#3498db', text: '#ffffff' },
    minimalist: { bg: '#f8f9fa', text: '#212529' },
    vibrant: { bg: '#e74c3c', text: '#ffffff' },
    corporate: { bg: '#2c3e50', text: '#ffffff' },
    artistic: { bg: '#9b59b6', text: '#ffffff' },
    vintage: { bg: '#d2b48c', text: '#5d4037' }
  };
  
  // Encode title for URL usage
  const encodedTitle = encodeURIComponent(title);
  
  // Get colors based on style
  const colorSet = colors[style] || colors.modern;
  
  // Create appropriate descriptive text based on asset type
  const typeText = type === 'banner' ? 'Banner' : 
                  type === 'social' ? 'Social Media Graphic' :
                  'Event Flyer';
  
  // Use placehold.co to generate a placeholder image
  return `https://placehold.co/${dimensions.width}x${dimensions.height}/${colorSet.bg.replace('#', '')}/${colorSet.text.replace('#', '')}?text=${encodedTitle}+-+${typeText}`;
};

/**
 * Service for generating visual assets using Stability AI
 */
const visualAssetService = {
  /**
   * Generate a banner for an event
   * 
   * @param request Asset generation request parameters
   * @returns Promise with the generated banner URL and metadata
   */
  generateEventBanner: async (request: VisualAssetRequest): Promise<VisualAssetResponse> => {
    try {
       // Default to banner dimensions if not specified
       const dimensions = request.dimensions || ASSET_DIMENSIONS.banner;

       // Create appropriate prompt for a banner
       const defaultPrompt = `Professional event banner for "${request.eventTitle}", a ${request.eventType} event. 
      Wide format banner image (${dimensions.width}x${dimensions.height}px). 
      Include visual elements related to: ${request.eventDescription.slice(0, 100)}. 
      Professional ${request.style} design with clear layout.`;

       // Use provided prompt or default
       const finalPrompt = request.prompt || defaultPrompt;

       // Get style preset
       const stylePreset = STYLE_PRESET_MAP[request.style];

       try {
          // Try to generate image with Stability AI
          const result = await generateImage({
             prompt: finalPrompt,
             negativePrompt: 'blurry, distorted, ugly, bad quality, text, watermark',
             stylePreset,
          });

          if (result && result.images && result.images.length > 0) {
             // Return the first image
             return {
                imageUrl: `data:image/png;base64,${result.images[0].base64}`,
                prompt: finalPrompt,
                metadata: {
                   type: 'banner',
                   dimensions,
                   style: request.style,
                   stylePreset,
                   seed: result.images[0].seed,
                   source: 'stabilityAI',
                },
             };
          }

          throw new Error('No images returned from Stability AI');
       } catch (apiError) {
          // Generate a placeholder image instead
          const placeholderUrl = generatePlaceholderImage(
             'banner',
             request.eventTitle,
             dimensions,
             request.style
          );

          return {
             imageUrl: placeholderUrl,
             prompt: finalPrompt,
             metadata: {
                type: 'banner',
                dimensions,
                style: request.style,
                stylePreset,
                source: 'placeholder',
                reason: 'API error or insufficient credits',
             },
          };
       }
    } catch (error) {
      console.error('Error generating event banner:', error);
      throw error;
    }
  },
  
  /**
   * Generate social media graphics for an event
   * 
   * @param request Asset generation request parameters
   * @param platform Optional platform to target (instagram, facebook, twitter, linkedin)
   * @returns Promise with the generated social graphic URL and metadata
   */
  generateSocialGraphic: async (
    request: VisualAssetRequest, 
    platform?: 'instagram' | 'facebook' | 'twitter' | 'linkedin'
  ): Promise<VisualAssetResponse> => {
    try {
       // Get appropriate dimensions for the platform
       const dimensions = platform
          ? ASSET_DIMENSIONS[platform]
          : request.dimensions || ASSET_DIMENSIONS.social;

       // Format date for prompt
       const formattedDate =
          request.eventDate instanceof Date
             ? request.eventDate.toLocaleDateString()
             : new Date(request.eventDate).toLocaleDateString();

       // Create platform-specific prompt
       const platformText = platform
          ? `optimized for ${platform}, in ${platform} post format, `
          : 'for social media, ';

       // Create appropriate prompt for a social media graphic
       const defaultPrompt = `Eye-catching social media graphic ${platformText}promoting "${request.eventTitle}" event on ${formattedDate} at ${request.eventLocation}. 
      Square format image (${dimensions.width}x${dimensions.height}px).
      Include visual elements that convey: ${request.eventType} event, ${request.eventDescription.slice(0, 80)}.
      ${request.style} design style, visually appealing, high engagement.`;

       // Use provided prompt or default
       const finalPrompt = request.prompt || defaultPrompt;

       // Get style preset
       const stylePreset = STYLE_PRESET_MAP[request.style];

       try {
          // Generate image with Stability AI
          const result = await generateImage({
             prompt: finalPrompt,
             negativePrompt: 'blurry, distorted, ugly, bad quality, text, watermark',
             stylePreset,
          });

          if (result && result.images && result.images.length > 0) {
             // Return the first image
             return {
                imageUrl: `data:image/png;base64,${result.images[0].base64}`,
                prompt: finalPrompt,
                metadata: {
                   type: 'social',
                   platform,
                   dimensions,
                   style: request.style,
                   stylePreset,
                   seed: result.images[0].seed,
                   source: 'stabilityAI',
                },
             };
          }

          throw new Error('No images returned from Stability AI');
       } catch (apiError) {
          // Use platform-specific placeholder if available, otherwise use generic
          const placeholderUrl = platform
             ? getPlaceholderImage(platform)
             : generatePlaceholderImage('social', request.eventTitle, dimensions, request.style);

          return {
             imageUrl: placeholderUrl,
             prompt: finalPrompt,
             metadata: {
                type: 'social',
                platform,
                dimensions,
                style: request.style,
                stylePreset,
                source: 'placeholder',
                reason: 'API error or insufficient credits',
             },
          };
       }
    } catch (error) {
      console.error('Error generating social graphic:', error);
      throw error;
    }
  },
  
  /**
   * Generate promotional materials (flyers, posters, etc.) for an event
   * 
   * @param request Asset generation request parameters
   * @returns Promise with the generated promotional material URL and metadata
   */
  generatePromotionalMaterial: async (request: VisualAssetRequest): Promise<VisualAssetResponse> => {
    try {
       // Default to flyer dimensions if not specified
       const dimensions = request.dimensions || ASSET_DIMENSIONS.flyer;

       // Format date for prompt
       const formattedDate =
          request.eventDate instanceof Date
             ? request.eventDate.toLocaleDateString()
             : new Date(request.eventDate).toLocaleDateString();

       // Create appropriate prompt for promotional material
       const defaultPrompt = `Professional event flyer design for "${request.eventTitle}" happening on ${formattedDate} at ${request.eventLocation}.
      Vertical format (${dimensions.width}x${dimensions.height}px) suitable for printing.
      Event type: ${request.eventType}.
      Include visual elements that represent: ${request.eventDescription.slice(0, 100)}.
      Clean ${request.style} design style with strong visual hierarchy.`;

       // Use provided prompt or default
       const finalPrompt = request.prompt || defaultPrompt;

       // Get style preset
       const stylePreset = STYLE_PRESET_MAP[request.style];

       try {
          // Generate image
          const result = await generateImage({
             prompt: finalPrompt,
             negativePrompt: 'blurry, distorted, ugly, bad quality, text, watermark',
             stylePreset,
          });

          if (result && result.images && result.images.length > 0) {
             // Return the first image
             return {
                imageUrl: `data:image/png;base64,${result.images[0].base64}`,
                prompt: finalPrompt,
                metadata: {
                   type: 'flyer',
                   dimensions,
                   style: request.style,
                   stylePreset,
                   seed: result.images[0].seed,
                   source: 'stabilityAI',
                },
             };
          }

          throw new Error('No images returned from Stability AI');
       } catch (apiError) {
          // Generate a placeholder image instead
          const placeholderUrl = generatePlaceholderImage(
             'flyer',
             request.eventTitle,
             dimensions,
             request.style
          );

          return {
             imageUrl: placeholderUrl,
             prompt: finalPrompt,
             metadata: {
                type: 'flyer',
                dimensions,
                style: request.style,
                stylePreset,
                source: 'placeholder',
                reason: 'API error or insufficient credits',
             },
          };
       }
    } catch (error) {
      console.error('Error generating promotional material:', error);
      throw error;
    }
  },
  
  /**
   * Save a generated visual asset to Firebase Storage
   * 
   * @param imageData Image data URL
   * @param orgId Organization ID
   * @param eventId Event ID
   * @param metadata Additional metadata
   * @returns Promise with the saved asset URL
   */
  saveAssetToStorage: async (
    imageData: string, 
    orgId: string, 
    eventId: string, 
    metadata: any
  ): Promise<string> => {
    try {
      // Check if the image is a data URL or a regular URL
      if (imageData.startsWith('data:')) {
         // Extract base64 data from the data URL
         const base64Data = imageData.split(',')[1];

         // Generate a unique filename
         const assetId = nanoid();
         const assetType = metadata.type || 'asset';
         const fileName = `${assetType}-${assetId}.png`;

         // Convert to file
         const file = base64ToFile(base64Data, fileName);

         // Create reference to storage location
         const storage = getStorage();
         const storageRef = ref(
            storage,
            `organizations/${orgId}/events/${eventId}/assets/${fileName}`
         );

         // Upload file
         await uploadBytes(storageRef, file);

         // Get download URL
         const downloadUrl = await getDownloadURL(storageRef);

         return downloadUrl;
      } else {
         // For external URLs (like placeholder images), we can't upload them directly,
         // so we just record the URL as-is

         // You could optionally fetch the external image and then save it to storage
         // but for simplicity, we'll just return the URL
         return imageData;
      }
    } catch (error) {
      console.error('Error saving asset to storage:', error);
      throw error;
    }
  },
  
  /**
   * Fetch all visual assets for an event
   * 
   * @param orgId Organization ID
   * @param eventId Event ID
   * @returns Promise with an array of saved assets
   */
  fetchEventAssets: async (orgId: string, eventId: string): Promise<SavedAsset[]> => {
    try {
       // First try to get assets from Firestore
       const assetsCollection = collection(db, 'events', eventId, 'assets');
       const assetsSnapshot = await getDocs(assetsCollection);

       if (!assetsSnapshot.empty) {
          // Return assets from Firestore
          const assets = assetsSnapshot.docs.map((doc) => {
             const data = doc.data();
             return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt || new Date().toISOString(),
             } as SavedAsset;
          });

          return assets;
       }

       // If no assets in Firestore, try to list them from Storage
       // This is a fallback for assets that were saved directly to Storage
       const storage = getStorage();
       const storageRef = ref(storage, `organizations/${orgId}/events/${eventId}/assets`);

       try {
          const result = await listAll(storageRef);

          // Map the storage items to assets
          const assets = await Promise.all(
             result.items.map(async (item) => {
                try {
                   const url = await getDownloadURL(item);
                   const filename = item.name;

                   // Extract asset type from filename if possible
                   let type: AssetType = 'banner';
                   if (filename.includes('banner-')) type = 'banner';
                   if (filename.includes('social-')) type = 'social';
                   if (filename.includes('flyer-') || filename.includes('material-'))
                      type = 'flyer';

                   // Create a basic asset object for items only in storage
                   const asset: SavedAsset = {
                      id: filename.split('.')[0],
                      imageUrl: url,
                      title: filename,
                      type,
                      createdAt: new Date().toISOString(),
                   };

                   // Also save this asset to Firestore for future reference
                   await addDoc(assetsCollection, {
                      ...asset,
                      storageRef: item.fullPath,
                   });

                   return asset;
                } catch (err) {
                   console.error(`Error processing storage item ${item.name}:`, err);
                   return null;
                }
             })
          );

          // Filter out any nulls
          const validAssets = assets.filter((asset) => asset !== null) as SavedAsset[];

          return validAssets;
       } catch (storageErr) {
          // If storage list fails, just return an empty array
          console.error('Error listing assets from storage:', storageErr);
          return [];
       }
    } catch (error) {
      console.error('Error fetching event assets:', error);
      return [];
    }
  },
  
  /**
   * Update an existing asset
   * 
   * @param eventId Event ID
   * @param asset Asset to update
   * @returns Promise indicating success
   */
  updateAsset: async (eventId: string, asset: SavedAsset): Promise<boolean> => {
    try {
      // Update the asset in Firestore
      const assetRef = doc(db, 'events', eventId, 'assets', asset.id);
      await updateDoc(assetRef, {
        ...asset,
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating asset:', error);
      return false;
    }
  },
  
  /**
   * Delete an asset
   * 
   * @param orgId Organization ID
   * @param eventId Event ID
   * @param assetId Asset ID
   * @returns Promise indicating success
   */
  deleteAsset: async (orgId: string, eventId: string, assetId: string): Promise<boolean> => {
    try {
      // Delete from Firestore first
      try {
        const assetRef = doc(db, 'events', eventId, 'assets', assetId);
        await deleteDoc(assetRef);
      } catch (firestoreErr) {
        console.error('Error deleting asset from Firestore:', firestoreErr);
        // Continue even if Firestore delete fails
      }
      
      // Then try to delete from Storage
      try {
        const storage = getStorage();
        const storageRef = ref(storage, `organizations/${orgId}/events/${eventId}/assets/${assetId}.png`);
        await deleteObject(storageRef);
      } catch (storageErr) {
        console.error('Error deleting asset from Storage:', storageErr);
        // Continue even if Storage delete fails
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting asset:', error);
      return false;
    }
  },
};

export default visualAssetService;
